<template>
    <div class="coach-calendar-availability">
        <div class="calendars-wrapper">
            <input v-model="selectedDate" name="date" style="display:none" type="date" />
            <!-- Rules: [no weekday, valid date format (YYYY-MM-DD), no null, min date tomorrow, max date 999 days from now] -->
        </div>

        <div class="page-header header-fixed">
            <h1>{{ __('New availability change') }} - {{ coach.user.fullname }}</h1>

            <div class="btn-group">
                <VButton :href="urls.redirect" :label="__('Cancel')" size="sm" variant="secondary" />
                <VButton :disabled="cancelledHours === 0 || cancelledHours - exchangedHours !== 0" :label="__('Confirm changes')" size="sm" type="submit" @onClick="submit" />
            </div>
        </div>

        <div v-if="isValidDate" class="availability-content">
            <div class="">
                <h2>{{ __('Cancelled date') }}</h2>
                <div class="calendar availability-cancelled">
                    <CalendarDay
                        :date="formattedSelectedDate"
                        :slots="
                            slotsGrid
                                .get()
                                .where('date', formattedSelectedDate)
                                .all()
                        "
                        :tooltip-table="cancelledDayTooltips"
                        mode="availability_cancelled"
                    />
                </div>

                <div class="availability-checkbox">
                    <h2>{{ __('Replacement date') }}</h2>
                    <div v-if="hasAnyAvailableDatesBeforeCancelledDate" class="checkbox-label-box">
                        <input id="before_check" v-model="showDatesBefore" name="type" type="checkbox" />
                        <label class="check-label__full" for="before_check">
                            <span>{{ __('Show dates before the cancelled date') }}</span>
                        </label>
                    </div>
                </div>

                <div :class="{ disabled: cancelledHours === 0 }" class="calendar availability-new">
                    <Calendar ref="Calendar" :exclude-dates="excludedDates" mode="availability_new" />
                </div>
            </div>

            <div class="availability-info">
                <div>
                    <span>{{ __('Cancelled') }}</span>
                    <strong>{{ numberFormat(cancelledHours, 1) }} {{ __('hr') }}</strong>
                </div>

                <div>
                    <span>{{ __('Replaced') }}</span>
                    <strong>{{ numberFormat(exchangedHours, 1) }} {{ __('hr') }}</strong>
                </div>

                <div class="availability-total availability-remain">
                    <span>{{ __('It remains to select') }}</span>
                    <strong>{{ numberFormat(cancelledHours - exchangedHours, 1) }} {{ __('hr') }}</strong>
                </div>

                <div v-if="(cancelledHours || exchangedHours) > 0" class="info-summary">
                    <strong class="summary-title">{{ __('Hour total') }}</strong>
                    <div class="summary-time">
                        <div
                            v-for="([start_time, end_time], i) in cancelledSlotsParsed"
                            :key="i"
                            class="info-changes cancelled"
                        >
                            <p>
                                {{ moment(formattedSelectedDate).format('dddd D. M.') }} {{ start_time.format('LT') }} -
                                {{ end_time.format('LT') }}
                            </p>
                        </div>

                        <div v-for="{ date, times } in exchangedSlotsParsed" :key="date">
                            <div v-for="([start_time, end_time], i) in times" :key="i" class="info-changes exchange">
                                <p>
                                    {{ moment(date).format('dddd D. M.') }} {{ start_time.format('LT') }} -
                                    {{ end_time.format('LT') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Calendar from '@/components/Calendar/Calendar.vue'
import CalendarDay from '@/components/Calendar/CalendarDay.vue'
import SlotsParser from '@/core/SlotsParser'

export default {
    components: {
        Calendar,
        CalendarDay
    },
    props: {
        coach: {
            type: Object,
            required: true
        },
        date: {
            type: String,
            required: false,
            default: null
        }
    },
    data() {
        return {
            selectedDate: this.date,
            showDatesBefore: false
        }
    },
    computed: {
        ...mapGetters({
            slotsGrid: 'general/slotsGrid'
        }),
        urls() {
            if (this.user.role === 'coach') {
                // for the coach editing his own availability
                return {
                    fetch: '/availability/calendar',
                    fetch_dates_before_cancelled_date: `/availability/calendar/dates-before-cancelled-date/${this.formattedSelectedDate}`,
                    store: '/availability',
                    redirect: '/dostupnost'
                }
            } else {
                // for the coach-admin editing the coach's availability
                return {
                    fetch: `/coaches/${this.coach.id}/availability/calendar`,
                    fetch_dates_before_cancelled_date: `/coaches/${this.coach.id}/availability/calendar/dates-before-cancelled-date/${this.formattedSelectedDate}`,
                    store: `/coaches/${this.coach.id}/availability`,
                    redirect: `/terapeut/${this.coach.id}#tab-availability-change`
                }
            }
        },
        formattedSelectedDate() {
            return moment(this.selectedDate).format('YYYY-MM-DD')
        },
        // returns collection of newly selected slots
        availabilityNew() {
            return this.slotsGrid.get().where('type', 'availability_new')
        },
        // returns collection of cancelled slots
        availabilityCancelled() {
            return this.slotsGrid.get().where('type', 'availability_cancelled')
        },
        // counts duration of cancelled slots
        cancelledHours() {
            return SlotsParser.use(this.availabilityCancelled).getDuration()
        },
        // counts duration of newly selected slots
        exchangedHours() {
            return SlotsParser.use(this.availabilityNew).getDuration()
        },
        // counts number of newly selected slots
        numberOfSelectedSlots() {
            return SlotsParser.use(this.availabilityNew).countSlots()
        },
        cancelledSlotsParsed() {
            return SlotsParser.use(this.availabilityCancelled).toTime()
        },
        exchangedSlotsParsed() {
            return SlotsParser.use(this.availabilityNew).toTimeCollection()
        },
        cancelledDayTooltips() {
            return {
                opening_hours: lang.get('Select the date being cancelled'),
                availability_cancelled: lang.get('Remove the date being cancelled'),
                default: lang.get('Time not available'),
                vacations: lang.get('Time not available'),
                consultations: lang.get('Consultation')
            }
        },
        // first day to replace cancelled slots for new is in three days from now
        firstAllowedDate() {
            if (this.user.country === 'DE') {
                return moment().add(1, 'days')
            } else {
                return moment().add(3, 'days')
            }
        },
        // check if we can show more dates for replacement
        hasAnyAvailableDatesBeforeCancelledDate() {
            return this.firstAllowedDate < moment(this.selectedDate)
        },
        // list of dates that can't be selected for replacement
        excludedDates() {
            const dates = []

            const startDate = moment()
            const endDate = moment(this.firstAllowedDate)

            while (true) {
                if (startDate.format('YYYY-MM-DD') >= endDate.format('YYYY-MM-DD')) {
                    break
                }

                dates.push(startDate.format('YYYY-MM-DD'))

                startDate.add(1, 'day')
            }

            return dates
        },
        isValidDate() {
            const date = this.selectedDate

            // date is considered invalid if the format is invalid or the date is not yet defined
            if (!date || !moment(date, 'YYYY-MM-DD').isValid()) {
                return false
            }

            // tomorrow is the minimum date
            return moment(date).isAfter(moment().startOf('day'), 'day')
        }
    },
    watch: {
        showDatesBefore() {
            if (this.showDatesBefore) {
                this.fetchDatesBeforeCancelledDate()
            } else {
                this.removeDatesBeforeCancelledDate()
            }
        }
    },
    mounted() {
        this.fetchSlots()

        this.slotsGrid.setOption('show-dates', true)
        this.slotsGrid.setOption('tooltip-table', {
            opening_hours: lang.get('Time not available'),
            vacations: lang.get('Time not available'),
            holidays: lang.get('Time not available'),
            availability_cancelled: lang.get('Time not available'),
            availability_new: lang.get('Remove the selected term'),
            default: lang.get('Choose a new time'),
            consultations: lang.get('Consultation')
        })

        // availability_new can't select slots containing opening_hours type
        this.slotsGrid.addToBlacklist('availability_new', 'opening_hours')
        this.slotsGrid.addToBlacklist('availability_new', 'consultations')
        this.slotsGrid.addToBlacklist('availability_new', 'vacations')
        this.slotsGrid.addToBlacklist('availability_new', 'holidays')

        // availability_cancelled can select only slots containing opening_hours type
        this.slotsGrid.addToWhitelist('availability_cancelled', 'opening_hours')
        this.slotsGrid.addToBlacklist('availability_cancelled', 'consultations')
        this.slotsGrid.addToBlacklist('availability_cancelled', 'vacations')

        // the number of newly selected slots must be equal to number of cancelled slots
        this.slotsGrid.addRule('availability_new', { maxSlots: 'availability_cancelled' })

        // this part isn't used anywhere but let's keep it for now
        // this.slotsGrid.setDisabledDates(
        //   _.range(0, 7).map(v => moment().subtract(v, 'day').format('YYYY-MM-DD'))
        // )
    },
    methods: {
        async fetchDatesBeforeCancelledDate() {
            const data = await axios.get(this.urls.fetch_dates_before_cancelled_date).thenData()

            this.loadSlotsGridWithData(data)
        },
        async fetchSlots() {
            const data = await axios
                .get(this.urls.fetch, {
                    params: {
                        date: this.prepareDateRange()
                    }
                })
                .thenData()

            this.loadSlotsGridWithData(data)
        },
        loadSlotsGridWithData(data) {
            const openingHours = data.map((item) => {
                item.type = item.type !== 'consultations' && item.type !== 'vacations' && item.type !== 'holidays' ? 'opening_hours' : item.type
                return item
            })

            this.slotsGrid.append(openingHours)
        },
        // removed added dates after unchecking checkbox 'showDatesBefore'
        removeDatesBeforeCancelledDate() {
            this.slotsGrid.filter((v) => v.date >= this.formattedSelectedDate)
        },
        // prepares range of dates is used for getting data for the calendar
        prepareDateRange() {
            const startDate = moment(this.selectedDate)
            const endDate = moment(this.selectedDate).add(7, 'days')

            return [startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')]
        },
        async submit() {
            const numberOfSelectedSlots = this.numberOfSelectedSlots
            const maxNumberOfSlots = SlotsParser.use(this.availabilityCancelled).countSlots()

            if (numberOfSelectedSlots === 0) {
                return alert('Zero slots were selected')
            }

            if (numberOfSelectedSlots < maxNumberOfSlots) {
                return alert('You need to select more slots')
            }

            if (numberOfSelectedSlots > maxNumberOfSlots) {
                return alert('You have selected to many slots')
            }

            await axios.post(this.urls.store, {
                data: this.slotsGrid.convertForBackend().toJson()
            })

            window.location.href = this.urls.redirect
        }
    }
}
</script>
